"use client";

import Link from "next/link";
import { AnimatePresence, motion } from "framer-motion";
import type { DestinationsUi } from "@/lib/content/pages/destinations";
import type { Destination } from "@/lib/destinations/types";
import DepthTilesLoop from "@/components/destinations/DepthTilesLoop";

type Props = {
  destination: Destination | null;
  onClose: () => void;
  locale: "fr" | "en";
  ui: DestinationsUi;
  contactHref: string;
};

export default function CityDetailPanel({
  destination,
  onClose,
  locale,
  ui,
  contactHref,
}: Props) {
  return (
    <AnimatePresence>
      {destination && (
        <motion.aside
          key={destination.id}
          initial={{ x: "100%", opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: "100%", opacity: 0 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="pointer-events-auto absolute right-0 top-0 z-30 flex h-full w-full max-w-[480px] flex-col bg-[#0b0b0d]/95 backdrop-blur-md border-l border-white/10"
          aria-label={`${destination.city} — ${
            locale === "fr" ? "détails de la destination" : "destination details"
          }`}
        >
          {/* Header du panel */}
          <div className="flex items-start justify-between border-b border-white/10 px-7 py-6">
            <div>
              <p className="text-[10px] tracking-[0.32em] uppercase text-white/45 font-body">
                {destination.country[locale]}
              </p>
              <h3 className="mt-2 font-heading text-4xl md:text-5xl font-light text-white leading-none">
                {destination.city}
              </h3>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label={locale === "fr" ? "Fermer" : "Close"}
              className="ml-4 inline-flex h-8 w-8 items-center justify-center rounded-full border border-white/15 text-white/70 hover:bg-white/10 hover:text-white transition"
            >
              <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.6">
                <path d="M6 6l12 12M6 18L18 6" strokeLinecap="round" />
              </svg>
            </button>
          </div>

          {/* Corps scrollable */}
          <div className="flex-1 overflow-y-auto px-7 py-6">
            <p className="text-sm leading-relaxed text-white/65 font-body font-light">
              {destination.intro[locale]}
            </p>

            {/* Hôtels — Depth Tiles Infinite Loop */}
            {destination.hotels.length > 0 && (
              <section className="mt-10">
                <h4 className="text-[10px] tracking-[0.32em] uppercase text-white/45 font-body">
                  {ui.hotelsTitle}
                </h4>
                <div className="mt-5">
                  <DepthTilesLoop
                    tiles={destination.hotels.map((h) => ({
                      id: h.id,
                      title: h.name,
                      category: h.category[locale],
                      description: h.description[locale],
                      imageSrc: h.imageSrc,
                    }))}
                  />
                </div>
              </section>
            )}

            {/* Activités — Depth Tiles Infinite Loop */}
            {destination.activities.length > 0 && (
              <section className="mt-10">
                <h4 className="text-[10px] tracking-[0.32em] uppercase text-white/45 font-body">
                  {ui.activitiesTitle}
                </h4>
                <div className="mt-5">
                  <DepthTilesLoop
                    tiles={destination.activities.map((a) => ({
                      id: a.id,
                      title: a.name[locale],
                      category: a.category[locale],
                      description: a.description[locale],
                      imageSrc: a.imageSrc,
                    }))}
                    intervalMs={4400}
                  />
                </div>
              </section>
            )}
          </div>

          {/* Footer CTA */}
          <div className="border-t border-white/10 px-7 py-5">
            <Link
              href={contactHref}
              className="flex items-center justify-between rounded-full bg-white px-6 py-3 text-xs tracking-[0.2em] uppercase text-black hover:opacity-90 transition-opacity"
            >
              <span>
                {ui.requestFor.replace("{city}", destination.city)}
              </span>
              <span className="ml-3">→</span>
            </Link>
          </div>
        </motion.aside>
      )}
    </AnimatePresence>
  );
}

